<!--
  ========================================================================
  LangSwitcher — seletor de idioma global (ES / PT / EN).
  ========================================================================
  Design idêntico ao usado no OligoscanLanding: pílula fixa no canto
  superior direito, botões circulares com bandeiras em escala de cinza
  que ficam coloridos no hover/ativo.

  Uso:
    <LangSwitcher v-model="currentLang" />

  Para páginas que possuem banner/alerta fixo no topo (ex: OligoscanLanding),
  ative o prop `evitarBannerMobile` para que o switcher desça para o
  rodapé direito em telas pequenas e não sobreponha o banner.

    <LangSwitcher v-model="currentLang" evitar-banner-mobile />

  Props:
    - modelValue            (String)  idioma atual ('es' | 'pt' | 'en')
    - idiomas               (Array)   lista de idiomas exibidos; default: ['es','pt','en']
    - evitarBannerMobile    (Boolean) em mobile vai para o bottom-right
    - onChange              (Function) emitido extra ao trocar (além de update:modelValue)

  Emite:
    - update:modelValue     — novo idioma escolhido
    - change                — idem, útil para marcar "escolha manual"
  ========================================================================
-->

<template>
  <div
    class="lang-switcher"
    :class="{ 'evita-banner-mobile': evitarBannerMobile }"
    role="group"
    aria-label="Language selector"
  >
    <button
      v-for="idioma in idiomasVisiveis"
      :key="idioma.code"
      type="button"
      :class="{ active: modelValue === idioma.code }"
      :title="idioma.titulo"
      :aria-label="idioma.titulo"
      :aria-pressed="modelValue === idioma.code"
      @click="escolher(idioma.code)"
    >
      {{ idioma.bandeira }}
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  modelValue: { type: String, default: 'es' },
  idiomas: {
    type: Array,
    default: () => ['es', 'pt', 'en']
  },
  evitarBannerMobile: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'change'])

const CATALOGO = {
  es: { code: 'es', bandeira: '🇪🇸', titulo: 'Español' },
  pt: { code: 'pt', bandeira: '🇧🇷', titulo: 'Português' },
  en: { code: 'en', bandeira: '🇺🇸', titulo: 'English' }
}

const idiomasVisiveis = computed(() =>
  props.idiomas
    .map(code => CATALOGO[code])
    .filter(Boolean)
)

const escolher = (code) => {
  if (props.modelValue === code) return
  emit('update:modelValue', code)
  emit('change', code)
}
</script>

<style scoped>
.lang-switcher {
  position: fixed;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 6px;
  z-index: 200;
  background: rgba(10, 26, 42, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 4px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.lang-switcher button {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  filter: grayscale(80%);
  opacity: 0.7;
  padding: 0;
  line-height: 1;
}

.lang-switcher button:hover,
.lang-switcher button.active {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--cor-azul, #14CBF0);
  filter: grayscale(0%);
  opacity: 1;
}

.lang-switcher button:focus-visible {
  outline: 2px solid var(--cor-azul, #14CBF0);
  outline-offset: 2px;
}

/* Responsivo base (usado por qualquer página) */
@media (max-width: 720px) {
  .lang-switcher { top: 10px; right: 10px; }
  .lang-switcher button { width: 28px; height: 28px; font-size: 14px; }
}

/* Modo especial: desce o switcher para o rodapé em mobile, para
   não sobrepor banners/alertas fixos no topo (ex: OligoscanLanding). */
@media (max-width: 900px) {
  .lang-switcher.evita-banner-mobile {
    top: auto;
    bottom: 16px;
    right: 16px;
  }
}

@media (max-width: 480px) {
  .lang-switcher.evita-banner-mobile {
    top: auto;
    bottom: 12px;
    right: 10px;
  }
}
</style>
